import React, { useEffect, useRef } from "react";

// Real-geography overview map (SLA OneMap tiles via Leaflet) for the sell-out rate showcase.
// Each project needs { id, lat, lng, label, badge, tone }.
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";

function loadLeaflet() {
  if (!document.getElementById("leaflet-css")) {
    const link = document.createElement("link");
    link.id = "leaflet-css";
    link.rel = "stylesheet";
    link.href = LEAFLET_CSS;
    document.head.appendChild(link);
  }
  if (window.L) return Promise.resolve(window.L);
  if (window.__leafletLoading) return window.__leafletLoading;
  window.__leafletLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = LEAFLET_JS;
    s.onload = () => resolve(window.L);
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return window.__leafletLoading;
}

const TONES = {
  active: { bg: "#0A1128", fg: "#FFFFFF", border: "#C5A880", badgeBg: "#C5A880", badgeFg: "#0A1128" },
  high: { bg: "rgba(255,255,255,0.96)", fg: "#0A1128", border: "#E7E2D6", badgeBg: "#D1FAE5", badgeFg: "#065F46" },
  mid: { bg: "rgba(255,255,255,0.96)", fg: "#0A1128", border: "#E7E2D6", badgeBg: "#FEF3C7", badgeFg: "#92400E" },
};

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function pinHtml({ label, badge, tone }) {
  const t = TONES[tone] || TONES.high;
  return `
    <div style="transform: translate(-50%, -100%); display:inline-flex; flex-direction:column; align-items:center; cursor:pointer;">
      <div style="display:flex; align-items:center; gap:6px; padding:3px 9px; border-radius:999px; white-space:nowrap;
                  background:${t.bg}; color:${t.fg}; border:1px solid ${t.border}; box-shadow:0 4px 10px rgba(10,17,40,0.18);
                  font: 700 12px/1.2 'Playfair Display', Georgia, serif;">
        <span>${esc(label)}</span>
        <span style="font: 700 10px/1 ui-monospace, monospace; padding:2px 5px; border-radius:4px; background:${t.badgeBg}; color:${t.badgeFg};">${esc(badge)}</span>
      </div>
      <div style="width:2px; height:8px; background:${t.border === "#C5A880" ? "#C5A880" : "#0A1128"};"></div>
      <div style="width:7px; height:7px; border-radius:50%; background:#0A1128; border:2px solid #fff; margin-top:-1px;"></div>
    </div>`;
}

export default function LaunchesOverviewMap({ projects, activeId, onSelect }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // Init once
  useEffect(() => {
    let cancelled = false;
    loadLeaflet().then((L) => {
      if (cancelled || !containerRef.current || mapRef.current) return;
      const map = L.map(containerRef.current, {
        center: [1.352, 103.82],
        zoom: 11,
        minZoom: 11,
        maxZoom: 17,
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: true,
      });
      L.tileLayer("https://www.onemap.gov.sg/maps/tiles/Grey/{z}/{x}/{y}.png", {
        detectRetina: true,
        maxZoom: 19,
        minZoom: 11,
        attribution: 'Map data &copy; <a href="https://www.onemap.gov.sg" target="_blank" rel="noreferrer">OneMap</a> Singapore Land Authority',
      }).addTo(map);
      map.setMaxBounds([[1.13, 103.55], [1.50, 104.15]]);
      const pts = projects.filter((p) => p.lat && p.lng).map((p) => [p.lat, p.lng]);
      if (pts.length) map.fitBounds(pts, { padding: [40, 40] });
      mapRef.current = map;
      renderMarkers();
    });
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markersRef.current = {}; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderMarkers() {
    const L = window.L;
    const map = mapRef.current;
    if (!L || !map) return;
    Object.values(markersRef.current).forEach((m) => m.remove());
    markersRef.current = {};
    projects.forEach((p) => {
      if (!p.lat || !p.lng) return;
      const isActive = p.id === activeId;
      const icon = L.divIcon({
        className: "asp-overview-pin",
        html: pinHtml({ label: p.label, badge: p.badge, tone: isActive ? "active" : p.tone }),
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      const m = L.marker([p.lat, p.lng], { icon, zIndexOffset: isActive ? 1000 : 0, title: p.label })
        .addTo(map)
        .on("click", () => onSelectRef.current && onSelectRef.current(p.id));
      markersRef.current[p.id] = m;
    });
  }

  // Re-render pins on selection and pan to the active project
  useEffect(() => {
    renderMarkers();
    const map = mapRef.current;
    const p = projects.find((x) => x.id === activeId);
    if (map && p && p.lat && p.lng) map.panTo([p.lat, p.lng], { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, projects]);

  return (
    <>
      <div ref={containerRef} className="absolute inset-0 z-0" />
      <style>{`.asp-overview-pin{background:transparent!important;border:none!important;}`}</style>
    </>
  );
}
